import React, { useState, useEffect, useRef } from 'react';
import { FiRefreshCw, FiUpload, FiX } from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../../services/api';

interface QRCustomizerProps {
  url: any;
  onQRGenerated: (qrCode: string) => void;
  onClose: () => void;
}

const QRCustomizer: React.FC<QRCustomizerProps> = ({ url, onQRGenerated, onClose }) => {
  const [fgColor, setFgColor] = useState('#1E40AF');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [errorCorrection, setErrorCorrection] = useState<'L' | 'M' | 'Q' | 'H'>('M');
  const [size, setSize] = useState(256);
  const [logo, setLogo] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [preview, setPreview] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Generate preview when settings change
  useEffect(() => {
    const timer = setTimeout(() => {
      generatePreview();
    }, 300); // Debounce to avoid too many API calls
    return () => clearTimeout(timer);
  }, [fgColor, bgColor, errorCorrection, size, logo]);

  const generatePreview = async () => {
    if (!url) return;
    
    try {
      const response = await api.post(`/urls/${url.hash}/qr/customize`, {
        fgColor,
        bgColor,
        errorCorrection,
        size,
        logo
      });
      
      setPreview(response.data.qrCode);
    } catch (error) {
      console.error('Failed to generate preview:', error);
    }
  };

  const handleApply = async () => {
    setIsGenerating(true);
    try {
      const response = await api.post(`/urls/${url.hash}/qr/customize`, {
        fgColor,
        bgColor,
        errorCorrection,
        size,
        logo,
        save: true // Save settings for this URL
      });
      
      onQRGenerated(response.data.qrCode);
      toast.success('QR code customized successfully!');
      onClose();
    } catch (error) {
      toast.error('Failed to customize QR code');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 100 * 1024) {
      toast.error('Logo must be less than 100KB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setLogo(event.target?.result as string);
      setLogoFile(file);
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogo(null);
    setLogoFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const presetColors = [
    { name: 'Default', fg: '#1E40AF', bg: '#FFFFFF' },
    { name: 'Dark', fg: '#FFFFFF', bg: '#000000' },
    { name: 'Green', fg: '#10B981', bg: '#ECFDF5' },
    { name: 'Purple', fg: '#7C3AED', bg: '#F3E8FF' },
    { name: 'Red', fg: '#DC2626', bg: '#FEF2F2' },
  ];

  return (
    <div className="space-y-4">
      {/* Preview */}
      <div className="bg-gray-50 rounded-lg p-8 flex items-center justify-center" style={{ minHeight: '320px' }}>
        {preview ? (
          <img 
            src={preview} 
            alt="QR Preview" 
            style={{ 
              width: Math.min(size, 300), 
              height: Math.min(size, 300),
              imageRendering: 'pixelated'
            }} 
          />
        ) : (
          <div className="w-64 h-64 bg-gray-200 animate-pulse rounded" />
        )}
      </div>

      {/* Color Presets */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Quick Presets
        </label>
        <div className="flex space-x-2">
          {presetColors.map((preset) => (
            <button
              key={preset.name}
              onClick={() => {
                setFgColor(preset.fg);
                setBgColor(preset.bg);
              }}
              className="px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-50 transition-colors"
              style={{
                borderColor: preset.fg,
                color: preset.fg,
                backgroundColor: preset.bg
              }}
            >
              {preset.name}
            </button>
          ))}
        </div>
      </div>

      {/* Custom Colors */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Foreground Color
          </label>
          <div className="flex space-x-2">
            <input
              type="color"
              value={fgColor}
              onChange={(e) => setFgColor(e.target.value)}
              className="h-10 w-20 border rounded cursor-pointer"
            />
            <input
              type="text"
              value={fgColor}
              onChange={(e) => setFgColor(e.target.value)}
              className="flex-1 px-3 py-2 border rounded-lg text-sm"
              placeholder="#000000"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Background Color
          </label>
          <div className="flex space-x-2">
            <input
              type="color"
              value={bgColor}
              onChange={(e) => setBgColor(e.target.value)}
              className="h-10 w-20 border rounded cursor-pointer"
            />
            <input
              type="text"
              value={bgColor}
              onChange={(e) => setBgColor(e.target.value)}
              className="flex-1 px-3 py-2 border rounded-lg text-sm"
              placeholder="#FFFFFF"
            />
          </div>
        </div>
      </div>

      {/* Logo Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Logo (Optional)
        </label>
        <div className="space-y-2">
          {logo ? (
            <div className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
              <img src={logo} alt="Logo" className="w-12 h-12 object-contain" />
              <span className="flex-1 text-sm text-gray-600 truncate">
                {logoFile?.name || 'Custom logo'}
              </span>
              <button
                onClick={removeLogo}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={handleLogoUpload}
                className="hidden"
                id="logo-upload"
              />
              <label
                htmlFor="logo-upload"
                className="flex items-center justify-center space-x-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 cursor-pointer transition-colors"
              >
                <FiUpload className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-600">Upload logo (max 100KB)</span>
              </label>
            </div>
          )}
          <p className="text-xs text-gray-500">
            Accepts PNG, JPEG, WebP • Logo will be embedded in the center. Use high error correction (H) for best results.
          </p>
        </div>
      </div>

      {/* Error Correction Level */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Error Correction Level
        </label>
        <div className="grid grid-cols-4 gap-2">
          {(['L', 'M', 'Q', 'H'] as const).map((level) => (
            <button
              key={level}
              onClick={() => setErrorCorrection(level)}
              className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                errorCorrection === level
                  ? 'bg-primary-50 border-primary-600 text-primary-600'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {level} ({level === 'L' ? '7%' : level === 'M' ? '15%' : level === 'Q' ? '25%' : '30%'})
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Higher levels allow more damage but make QR code denser. Use H (30%) when adding a logo.
        </p>
      </div>

      {/* Size */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Size: {size}px
        </label>
        <input
          type="range"
          min="128"
          max="512"
          step="32"
          value={size}
          onChange={(e) => setSize(Number(e.target.value))}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>128px</span>
          <span>256px</span>
          <span>384px</span>
          <span>512px</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-2 pt-4 border-t">
        <button
          onClick={handleApply}
          disabled={isGenerating}
          className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-primary-600 text-white rounded-lg hover:from-purple-700 hover:to-primary-700 transition-colors disabled:opacity-50"
        >
          {isGenerating ? (
            <>
              <FiRefreshCw className="w-4 h-4 animate-spin" />
              <span>Generating...</span>
            </>
          ) : (
            <span>Apply Customization</span>
          )}
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default QRCustomizer;